import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePortalSession } from '../../context/PortalContext';

const ClientAuthGuard = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { loading: portalLoading, error } = usePortalSession();
  const [isPublicBooking, setIsPublicBooking] = useState(false);

  useEffect(() => {
    // Check if this is a public booking (publicBookingId in localStorage)
    const publicBookingId = localStorage.getItem('public_booking_id');
    
    // Public booking mode: allow unauthenticated access to booking pages
    if (publicBookingId && location.pathname.startsWith('/app/book')) {
      setIsPublicBooking(true);
      return; // Skip authentication check
    }

    // Wait for auth to load
    if (authLoading) return;

    // If not authenticated and not a public booking, redirect to home
    if (!user) {
      navigate('/?auth=1');
      return;
    }

    // If not a client, redirect to admin
    if (user.role !== 'client') {
      navigate('/admin');
      return;
    }
  }, [user, authLoading, navigate, location.pathname]);

  // Public booking mode: skip all auth checks
  if (isPublicBooking) {
    return children;
  }

  // Show loading skeleton while loading
  if (authLoading || portalLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse">
          <div className="h-8 w-64 bg-gray-300 rounded mb-4"></div>
          <div className="h-4 w-48 bg-gray-300 rounded mb-2"></div>
          <div className="h-4 w-56 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  // Show error banner if portal data failed to load
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="h-12 w-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-lg font-medium">Failed to load portal data</p>
            <p className="text-sm text-gray-600 mt-1">{error}</p>
          </div>
          <button
            onClick={() => navigate('/?auth=1')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // If user is not a client, show loading (will redirect)
  if (user && user.role !== 'client') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse">
          <div className="h-8 w-32 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  // Render children if authenticated and is client
  return children;
};

export default ClientAuthGuard;
